import {
  CloudUploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FolderOpenOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Typography,
  Upload,
} from 'antd'
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import FileItem from '../../components/Common/FileItemComponent/FileItem'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PageTitle from '../../components/PageTitle'
import { systemName } from '../../config'
import { ACL_ACTION_TYPE } from '../../constants'
import { Container } from '../../layouts/Container/Container'
import DashboardLayout from '../../layouts/DashboardLayout'
import { FileRequest } from '../../requests/FileRequest'
import aclStore from '../../stores/aclStore'
import fileStore from '../../stores/fileStore'
import utils from '../../utils'
import {
  ContractManagementRequest,
  mapApiContractToUi,
  mapFormValuesToContractPayload,
  mapServiceFormToApi,
  parseAttachmentsToUploadFiles,
  parseContractToFormValues,
} from './contractApi'
import {
  CONTRACT_STATUS,
  CONTRACT_STATUS_OPTIONS,
  formatCurrencyVN,
} from './mockData'

const { TextArea } = Input
const { Option } = Select
const { RangePicker } = DatePicker

const getAttachmentTypeLabel = fileName => {
  const extension = String(utils.getExtensionFile(fileName || '') || '')
    .trim()
    .toUpperCase()

  if (!extension) return 'FILE'
  if (extension.length <= 4) return extension
  return extension.slice(0, 4)
}

const createDefaultServices = () => {
  return [
    {
      serviceType: '',
      value: undefined,
      effectiveRange: undefined,
      note: '',
    },
  ]
}

const extractUploadedFileId = response => {
  return response?.data?.file_id || response?.data?.file_summary?.file_id
}

const calculateTotalServiceValue = services => {
  return (services || []).reduce((sum, service) => {
    return sum + Number(service?.value || 0)
  }, 0)
}

const ContractManagementFormPage = props => {
  const { history, match, location } = props
  const [form] = Form.useForm()
  const [fileList, setFileList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [originalStatus, setOriginalStatus] = useState(CONTRACT_STATUS.ACTIVE)
  const [originalDetails, setOriginalDetails] = useState([])
  const draggerContainerRef = useRef(null)

  const { contractId } = match.params
  const isCreateMode = location.pathname.includes('/create')
  const isEditMode = location.pathname.includes('/edit/')
  const canCreateContract = aclStore.checkAccessControlAction(
    ACL_ACTION_TYPE.create__CONTRACT
  )
  const canEditContract = aclStore.checkAccessControlAction(
    ACL_ACTION_TYPE.edit__CONTRACT
  )
  const canSubmitContract =
    (isCreateMode && canCreateContract) || (isEditMode && canEditContract)

  const formTitle = useMemo(() => {
    return isCreateMode ? 'Tạo hợp đồng mới' : 'Chỉnh sửa hợp đồng'
  }, [isCreateMode])

  const initializeCreateForm = useCallback(() => {
    setOriginalStatus(CONTRACT_STATUS.ACTIVE)
    setOriginalDetails([])
    setFileList([])
    form.resetFields()
    form.setFieldsValue({
      contractNo: '',
      status: undefined,
      signedDate: undefined,
      effectiveDate: undefined,
      supplierName: '',
      customerName: '',
      description: '',
      services: createDefaultServices(),
    })
  }, [form])

  useEffect(() => {
    if (!aclStore.aclActionsByUser.length) return
    if (isCreateMode && !canCreateContract) {
      message.error('Bạn không có quyền tạo hợp đồng')
      history.push('/utility/contracts')
      return
    }
    if (isEditMode && !canEditContract) {
      message.error('Bạn không có quyền chỉnh sửa hợp đồng')
      history.push('/utility/contracts')
    }
  }, [
    aclStore.aclActionsByUser.length,
    canCreateContract,
    canEditContract,
    history,
    isCreateMode,
    isEditMode,
  ])

  const initializeEditForm = useCallback(async () => {
    setIsLoading(true)
    try {
      const { data } = await ContractManagementRequest.getContractById(
        contractId
      )
      const contract = mapApiContractToUi(data)

      setOriginalStatus(contract.status || CONTRACT_STATUS.ACTIVE)
      setOriginalDetails(contract.services || [])
      setFileList(parseAttachmentsToUploadFiles(contract.documents))
      form.setFieldsValue(parseContractToFormValues(contract))
    } catch (error) {
      console.error(error)
      history.push('/utility/contracts')
    } finally {
      setIsLoading(false)
    }
  }, [contractId, form, history])

  useEffect(() => {
    if (isCreateMode) {
      initializeCreateForm()
      return
    }
    initializeEditForm()
  }, [initializeCreateForm, initializeEditForm, isCreateMode])

  const handleFileChange = useCallback(info => {
    const normalizedFiles = info.fileList.map(file => ({
      ...file,
      fileId: file.fileId || file.response?.file_id || '',
    }))
    setFileList(normalizedFiles)
  }, [])

  const removeFileByUid = useCallback(fileUid => {
    setFileList(previousFiles =>
      previousFiles.filter(file => String(file.uid) !== String(fileUid))
    )
  }, [])

  const downloadAttachment = useCallback(file => {
    if (!file?.fileId) return
    fileStore.handleDownloadFile(file.fileId, file.name)
  }, [])

  const openFileDialogFromButton = useCallback(event => {
    event.preventDefault()
    event.stopPropagation()
    const fileInput = draggerContainerRef.current?.querySelector(
      'input[type="file"]'
    )
    fileInput?.click()
  }, [])

  const openPreview = useCallback(() => {
    const values = form.getFieldsValue()
    const previewTotalValue = calculateTotalServiceValue(values.services)
    const statusLabel =
      CONTRACT_STATUS_OPTIONS.find(option => option.value === values.status)
        ?.label ||
      values.status ||
      '-'
    Modal.info({
      title: 'Xem trước hợp đồng',
      width: 640,
      content: (
        <div>
          <p>
            <strong>Số hợp đồng:</strong> {values.contractNo || '-'}
          </p>
          <p>
            <strong>Trạng thái:</strong> {statusLabel}
          </p>
          <p>
            <strong>Bên A:</strong> {values.supplierName || '-'}
          </p>
          <p>
            <strong>Bên B:</strong> {values.customerName || '-'}
          </p>
          <p>
            <strong>Ngày ký:</strong>{' '}
            {values.signedDate?.format('DD/MM/YYYY') || '-'}
          </p>
          <p>
            <strong>Tổng giá trị:</strong> {formatCurrencyVN(previewTotalValue)}
          </p>
          <p>
            <strong>Số dịch vụ:</strong> {values.services?.length || 0}
          </p>
          <Divider style={{ margin: '8px 0' }} />
          <p>
            <strong>Tài liệu đính kèm ({fileList.length} file):</strong>
          </p>
          {fileList.length ? (
            <div
              style={{
                maxHeight: 220,
                overflowY: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                padding: '4px 0',
              }}>
              {fileList.map(file => (
                <div
                  key={file.uid}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    borderBottom: '1px solid #f9f9f9',
                  }}>
                  <FileItem
                    file_id={file.fileId}
                    file_name={file.name}
                    file_type={utils.getExtensionFile(file.name || '')}
                    noAction={!file.fileId}
                    noPreview={!file.fileId}
                  />
                </div>
              ))}
            </div>
          ) : (
            <Typography.Text type="secondary">
              Chưa có tài liệu đính kèm
            </Typography.Text>
          )}
        </div>
      ),
    })
  }, [fileList, form])

  const uploadPendingFiles = useCallback(async () => {
    const existingFileIds = []
    const pendingFiles = []

    fileList.forEach(file => {
      if (file.fileId) {
        existingFileIds.push(file.fileId)
        return
      }
      if (file.originFileObj) {
        pendingFiles.push(file)
      }
    })

    if (!pendingFiles.length) {
      return [...new Set(existingFileIds.filter(Boolean))]
    }

    const uploadResponses = await Promise.all(
      pendingFiles.map(file => {
        const formData = new FormData()
        formData.append('file', file.originFileObj)
        return FileRequest.uploadFile(formData)
      })
    )

    const uploadedFileIds = uploadResponses
      .map(extractUploadedFileId)
      .filter(Boolean)

    return [...new Set([...existingFileIds, ...uploadedFileIds])]
  }, [fileList])

  const syncContractDetails = useCallback(
    async services => {
      if (!isEditMode) return

      const existingDetailIds = new Set(
        (originalDetails || [])
          .map(detail => (detail?.id ? String(detail.id) : ''))
          .filter(Boolean)
      )
      const nextDetailIds = new Set()
      const requests = []

      ;(services || []).forEach(service => {
        const detailId = service?.serviceId ? String(service.serviceId) : ''
        const payload = mapServiceFormToApi(service)

        if (detailId && existingDetailIds.has(detailId)) {
          nextDetailIds.add(detailId)
          requests.push(
            ContractManagementRequest.updateContractDetail(
              contractId,
              detailId,
              payload
            )
          )
          return
        }

        requests.push(
          ContractManagementRequest.createContractDetail(contractId, payload)
        )
      })
      ;(originalDetails || []).forEach(detail => {
        const detailId = detail?.id ? String(detail.id) : ''
        if (!detailId || nextDetailIds.has(detailId)) return
        requests.push(
          ContractManagementRequest.deleteContractDetail(contractId, detailId)
        )
      })

      if (!requests.length) return
      await Promise.all(requests)
    },
    [contractId, isEditMode, originalDetails]
  )

  const handleSubmit = useCallback(
    async values => {
      if (!canSubmitContract) {
        message.error('Bạn không có quyền thực hiện thao tác này')
        return
      }
      try {
        setIsSubmitting(true)
        const attachmentIds = await uploadPendingFiles()

        const payload = mapFormValuesToContractPayload({
          values,
          status: values.status || originalStatus || CONTRACT_STATUS.ACTIVE,
          attachmentIds,
        })

        if (isEditMode) {
          await ContractManagementRequest.updateContract(contractId, payload)
          await syncContractDetails(values.services || [])
          message.success('Cập nhật hợp đồng thành công')
        } else {
          await ContractManagementRequest.createContract(payload)
          message.success('Tạo hợp đồng thành công')
        }

        history.push('/utility/contracts')
      } catch (error) {
        console.error(error)
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      contractId,
      history,
      isEditMode,
      originalStatus,
      canSubmitContract,
      syncContractDetails,
      uploadPendingFiles,
    ]
  )

  const serviceColumns = remove => [
    {
      title: 'STT',
      width: 60,
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Loại dịch vụ',
      width: 220,
      render: (_, field) => (
        <Form.Item
          name={[field.name, 'serviceType']}
          style={{ marginBottom: 0 }}
          rules={[{ required: true, message: 'Nhập loại dịch vụ' }]}>
          <Input placeholder="Nhập loại dịch vụ" />
        </Form.Item>
      ),
    },
    {
      title: 'Giá trị (VND)',
      width: 180,
      render: (_, field) => (
        <Form.Item
          name={[field.name, 'value']}
          style={{ marginBottom: 0 }}
          rules={[{ required: true, message: 'Nhập giá trị' }]}>
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            formatter={value =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={value => (value ? value.replace(/\D/g, '') : '')}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Thời gian hiệu lực',
      width: 280,
      render: (_, field) => (
        <Form.Item
          name={[field.name, 'effectiveRange']}
          style={{ marginBottom: 0 }}
          rules={[{ required: true, message: 'Chọn thời gian hiệu lực' }]}>
          <RangePicker style={{ width: '100%' }} format={'DD/MM/YYYY'} />
        </Form.Item>
      ),
    },
    {
      title: 'Ghi chú / Quy định riêng',
      render: (_, field) => (
        <Form.Item name={[field.name, 'note']} style={{ marginBottom: 0 }}>
          <Input placeholder="Ghi chú dịch vụ" />
        </Form.Item>
      ),
    },
    {
      title: 'Thao tác',
      width: 90,
      align: 'center',
      render: (_, field) => (
        <Popconfirm
          title="Xóa dịch vụ này?"
          okText="Xóa"
          cancelText="Hủy"
          onConfirm={() => remove(field.name)}>
          <Button danger type="link" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ]

  return (
    <DashboardLayout>
      <Helmet>
        <title>
          {formTitle} | {systemName} Portal
        </title>
      </Helmet>
      <PageTitle location={props.location} title={formTitle} showTitle={true} />
      <ContentBlockWrapper>
        <Container maxWidth={1300}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            scrollToFirstError={true}>
            <Card
              title="1. Thông tin chung hợp đồng"
              bordered={false}
              style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col md={6} xs={24}>
                  <Form.Item
                    label="Số hợp đồng"
                    name="contractNo"
                    rules={[{ required: true, message: 'Nhập số hợp đồng' }]}>
                    <Input placeholder="VD: HD-2026-00045" />
                  </Form.Item>
                </Col>
                <Col md={6} xs={24}>
                  <Form.Item
                    label="Trạng thái hợp đồng"
                    name="status"
                    rules={[
                      { required: true, message: 'Chọn trạng thái hợp đồng' },
                    ]}>
                    <Select placeholder="Chọn trạng thái">
                      {CONTRACT_STATUS_OPTIONS.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col md={6} xs={24}>
                  <Form.Item
                    label="Ngày ký kết"
                    name="signedDate"
                    rules={[{ required: true, message: 'Chọn ngày ký kết' }]}>
                    <DatePicker
                      style={{ width: '100%' }}
                      format={'DD/MM/YYYY'}
                    />
                  </Form.Item>
                </Col>
                <Col md={6} xs={24}>
                  <Form.Item
                    label="Ngày hiệu lực tổng hợp đồng"
                    name="effectiveDate">
                    <DatePicker
                      style={{ width: '100%' }}
                      format={'DD/MM/YYYY'}
                    />
                  </Form.Item>
                </Col>

                <Col md={12} xs={24}>
                  <Form.Item
                    label="Bên A (Nhà cung cấp)"
                    name="supplierName"
                    rules={[{ required: true, message: 'Nhập tên bên A' }]}>
                    <Input placeholder="Nhập tên bên A" />
                  </Form.Item>
                </Col>
                <Col md={12} xs={24}>
                  <Form.Item
                    label="Bên B (Khách hàng)"
                    name="customerName"
                    rules={[{ required: true, message: 'Nhập tên bên B' }]}>
                    <Input placeholder="Nhập tên bên B" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    label="Mô tả hợp đồng / Điều khoản chung"
                    name="description">
                    <TextArea
                      rows={3}
                      placeholder="Nhập mô tả chung hợp đồng"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card
              title="2. Chi tiết dịch vụ (nhiều loại - giá trị và thời gian hiệu lực khác nhau)"
              bordered={false}
              style={{ marginBottom: 16 }}>
              <Form.List name="services">
                {(fields, { add, remove }) => (
                  <>
                    <Table
                      columns={serviceColumns(remove)}
                      dataSource={fields}
                      rowKey="key"
                      pagination={false}
                      scroll={{ x: 1200 }}
                    />
                    <div style={{ marginTop: 12 }}>
                      <Button
                        icon={<PlusOutlined />}
                        onClick={() =>
                          add({
                            serviceType: '',
                            value: undefined,
                            effectiveRange: undefined,
                            note: '',
                          })
                        }>
                        Thêm dịch vụ mới
                      </Button>
                    </div>
                  </>
                )}
              </Form.List>
              <Divider />
              <div style={{ textAlign: 'right' }}>
                <Form.Item noStyle shouldUpdate>
                  {() => {
                    const currentTotalValue = calculateTotalServiceValue(
                      form.getFieldValue('services')
                    )

                    return (
                      <Typography.Text strong>
                        TỔNG GIÁ TRỊ HỢP ĐỒNG:{' '}
                        {formatCurrencyVN(currentTotalValue)}
                      </Typography.Text>
                    )
                  }}
                </Form.Item>
              </div>
            </Card>

            <Card title="3. Tài liệu đính kèm" bordered={false}>
              <div ref={draggerContainerRef}>
                <Upload.Dragger
                  multiple
                  beforeUpload={() => false}
                  showUploadList={false}
                  openFileDialogOnClick={false}
                  fileList={fileList}
                  onChange={handleFileChange}
                  style={{
                    marginBottom: 16,
                    background: '#f3f5f8',
                    border: '1px dashed #2f6bdb',
                    borderRadius: 4,
                    padding: '18px 12px',
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 10,
                    }}>
                    <CloudUploadOutlined
                      style={{ fontSize: 44, color: '#1677ff', lineHeight: 1 }}
                    />
                    <Typography.Text
                      strong
                      style={{
                        color: '#1f1f1f',
                        fontSize: 15,
                        lineHeight: '22px',
                      }}>
                      Kéo thả file vào đây hoặc
                    </Typography.Text>
                    <Button
                      type="primary"
                      icon={<FolderOpenOutlined />}
                      onClick={openFileDialogFromButton}
                      style={{
                        borderRadius: 4,
                        height: 30,
                        padding: '0 14px',
                      }}>
                      Chọn file từ máy tính
                    </Button>
                  </div>
                  <Typography.Text
                    type="secondary"
                    style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                    Hỗ trợ: PDF, JPG, PNG (tối đa 50MB mỗi file)
                  </Typography.Text>
                </Upload.Dragger>
              </div>

              {fileList.length ? (
                <>
                  <Typography.Text
                    strong
                    style={{ display: 'block', marginBottom: 8 }}>
                    Tài liệu đã đính kèm ({fileList.length})
                  </Typography.Text>
                  <div
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      overflow: 'hidden',
                    }}>
                    {fileList.map((file, index) => (
                      <div
                        key={file.uid}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          padding: '10px 14px',
                          borderBottom:
                            index === fileList.length - 1
                              ? 'none'
                              : '1px solid #f0f0f0',
                          backgroundColor: '#fff',
                        }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            flex: 1,
                            minWidth: 0,
                          }}>
                          <div
                            style={{
                              width: 44,
                              textAlign: 'center',
                              color: '#ff4d4f',
                              flexShrink: 0,
                              lineHeight: 1,
                            }}>
                            <FilePdfOutlined style={{ fontSize: 30 }} />
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                marginTop: 2,
                              }}>
                              {getAttachmentTypeLabel(file.name)}
                            </div>
                          </div>
                          <div style={{ marginLeft: 12, minWidth: 0 }}>
                            <Typography.Text
                              strong
                              style={{
                                display: 'block',
                                color: '#262626',
                                cursor: file.fileId ? 'pointer' : 'default',
                              }}
                              ellipsis={{ tooltip: file.name }}
                              onClick={() => downloadAttachment(file)}>
                              {file.name || 'Tệp đính kèm'}
                            </Typography.Text>
                          </div>
                        </div>

                        <Button
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => removeFileByUid(file.uid)}
                          style={{
                            width: 34,
                            height: 34,
                            border: '1px solid #ffccc7',
                            borderRadius: 6,
                            color: '#ff4d4f',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : null}

              <Divider />
              <div style={{ textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => history.push('/utility/contracts')}>
                    Hủy
                  </Button>
                  <Button icon={<EyeOutlined />} onClick={openPreview}>
                    Xem trước
                  </Button>
                  {canSubmitContract && (
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={isSubmitting}>
                      Lưu hợp đồng
                    </Button>
                  )}
                </Space>
              </div>
            </Card>
          </Form>
        </Container>
      </ContentBlockWrapper>
    </DashboardLayout>
  )
}

export default memo(ContractManagementFormPage)
